import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import './DashboardPages.css';

const STATUS_LABELS = {
  pending: 'Pending',
  assigned: 'Assigned',
  in_transit: 'In Transit',
  delivered: 'Delivered',
};

const STATUS_STEPS = ['pending', 'assigned', 'in_transit', 'delivered'];

const POLL_MS = 30000;

function OrderStatusProgress({ status }) {
  const currentIndex = STATUS_STEPS.indexOf(status);
  const safeIndex = currentIndex >= 0 ? currentIndex : 0;

  return (
    <div className="order-status-progress" aria-label="Order status progression">
      {STATUS_STEPS.map((step, index) => (
        <React.Fragment key={step}>
          <span
            className={[
              'osp-step',
              index < safeIndex ? 'osp-done' : '',
              index === safeIndex ? 'osp-current' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {STATUS_LABELS[step]}
          </span>
          {index < STATUS_STEPS.length - 1 && (
            <span className={`osp-connector ${index < safeIndex ? 'osp-connector-done' : ''}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [assignModal, setAssignModal] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadOrders = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError(null);
      const { orders: data } = await api.getOrders(filter || undefined);
      setOrders(data || []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
      if (!silent) setOrders([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const id = setInterval(() => {
      loadOrders({ silent: true });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [loadOrders]);

  const openAssignModal = async (order) => {
    try {
      const { drivers: list } = await api.getDrivers();
      setDrivers(list);
      setAssignModal(order);
    } catch (err) {
      setError(err.message);
    }
  };

  const assignDriver = async (driverId) => {
    if (!assignModal) return;
    try {
      setAssigning(true);
      setError(null);
      await api.createTrip(assignModal.order_id, driverId);
      setAssignModal(null);
      await loadOrders({ silent: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="dashboard-page">
      <h1>Orders</h1>
      <p className="dashboard-subtitle">
        Transport requests from WhatsApp and the dashboard — statuses update as drivers progress
      </p>

      <div className="orders-toolbar">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="orders-filter"
          aria-label="Filter orders by status"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="assigned">Assigned</option>
          <option value="in_transit">In Transit</option>
          <option value="delivered">Delivered</option>
        </select>
        <button
          type="button"
          className="orders-refresh-btn"
          onClick={() => loadOrders({ silent: true })}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
        {lastUpdated && (
          <span className="orders-last-updated">
            Updated {formatDateTime(lastUpdated)}
          </span>
        )}
      </div>

      {error && (
        <div className="orders-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="orders-placeholder">
          <p>Loading orders…</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="orders-placeholder">
          <p>No orders yet.</p>
          <p className="orders-hint">
            When a farmer completes an order on WhatsApp, it appears here as <strong>Pending</strong>.
            Assign a driver to move to <strong>Assigned</strong>; pickup and delivery confirmations
            (WhatsApp or trip updates) move the order to <strong>In Transit</strong> and{' '}
            <strong>Delivered</strong>.
          </p>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((o) => (
            <div key={o.order_id} className="order-card">
              <div className="order-card-header">
                <span className="order-id">#{o.order_id}</span>
                <span className="order-source">WhatsApp / API</span>
                <span className={`order-status status-${o.status}`}>
                  {STATUS_LABELS[o.status] || o.status}
                </span>
              </div>

              <OrderStatusProgress status={o.status} />

              <div className="order-card-body">
                <p><strong>Received:</strong> {formatDateTime(o.created_at)}</p>
                <p><strong>Pickup:</strong> {o.pickup_location}</p>
                <p><strong>Delivery:</strong> {o.delivery_location}</p>
                <p><strong>Produce:</strong> {o.produce_type || '—'}</p>
                <p><strong>Weight:</strong> {o.weight || '—'}</p>
                <p><strong>Pickup date:</strong> {formatDate(o.pickup_date)}</p>
                <p><strong>Farmer:</strong> {o.farmer_name || '—'} {o.farmer_phone && `(${o.farmer_phone})`}</p>
                {o.driver_name && (
                  <p><strong>Driver:</strong> {o.driver_name} {o.driver_phone && `(${o.driver_phone})`}</p>
                )}
                {o.trip_id && (
                  <p><strong>Trip:</strong> #{o.trip_id} — {STATUS_LABELS[o.trip_status] || o.trip_status || '—'}</p>
                )}
                {o.quote_distance_km != null && (
                  <p><strong>Quote distance:</strong> {o.quote_distance_km} km</p>
                )}
                <p>
                  <strong>Cold chain:</strong>{' '}
                  {o.cold_chain === true ? 'Yes' : o.cold_chain === false ? 'No' : '—'}
                </p>
                {o.price != null && (
                  <p><strong>Price:</strong> {Number(o.price).toLocaleString()} FCFA</p>
                )}
              </div>
              {o.status === 'pending' && (
                <button
                  type="button"
                  className="order-assign-btn"
                  onClick={() => openAssignModal(o)}
                >
                  Assign driver
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {assignModal && (
        <div className="modal-overlay" onClick={() => !assigning && setAssignModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Assign driver to order #{assignModal.order_id}</h3>
            <p className="orders-hint">
              After assignment, order status becomes <strong>Assigned</strong>. Driver pickup moves it to{' '}
              <strong>In Transit</strong>; delivery completes as <strong>Delivered</strong>.
            </p>
            {drivers.length === 0 ? (
              <p className="orders-hint">No drivers available. Add drivers on the Drivers page first.</p>
            ) : (
              <ul className="driver-list">
                {drivers.map((d) => (
                  <li key={d.driver_id}>
                    <button
                      type="button"
                      className="driver-select-btn"
                      onClick={() => assignDriver(d.driver_id)}
                      disabled={assigning}
                    >
                      {d.name} — {d.phone}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setAssignModal(null)}
              disabled={assigning}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersPage;
